import React, { Component } from 'react';
import { t } from '../i18n';
import LcIcon from '../widget/lc-icon.js';
import nativeBridge, { openLink } from '../widget/native-bridge.js';

export default class BindingMessage extends Component {
    handleCopyCode = () => {
        const { code } = this.props.bindingData;
        if (code && navigator.clipboard) {
            navigator.clipboard.writeText(`/verify ${code}`).then(() => {
                alert(t('binding.copied'));
            }).catch(err => {
                console.error('Failed to copy:', err);
            });
        }
    }

    handleEnablePush = async () => {
        try {
            // 从window获取pushManager实例
            const pushManager = window.laneChatPushManager;
            if (!pushManager) {
                alert(t('binding.push_not_ready'));
                return;
            }

            // 先检查当前权限状态
            const currentPermission = Notification.permission;
            if (currentPermission === 'denied') {
                // 权限已被拒绝，显示详细指导
                const message = this.getPermissionDeniedMessage();
                alert(message);

                // 更新UI显示权限被拒绝的状态
                const chat = window.laneChatInstance;
                if (chat) {
                    chat.writeToMessages({
                        from: 'system',
                        text: '',
                        type: 'binding',
                        binding_data: {
                            binding_type: 'push',
                            status: 'permission-denied',
                            message: t('binding.push_denied_status_message')
                        },
                        time: new Date()
                    });
                }
                return;
            }

            await pushManager.subscribe();
            // 订阅成功后会自动更新状态
        } catch (error) {
            if (error.message === 'Permission denied' || error.message === 'Notifications permission denied') {
                const message = this.getPermissionDeniedMessage();
                alert(message);
            } else {
                alert(t('binding.push_enable_failed', { error: error.message }));
            }
        }
    }

    getPermissionDeniedMessage = () => {
        const userAgent = navigator.userAgent;
        let message = t('binding.permission_denied_intro');

        if (userAgent.includes('Chrome') && !userAgent.includes('Edge')) {
            message += t('binding.permission_chrome');
        } else if (userAgent.includes('Firefox')) {
            message += t('binding.permission_firefox');
        } else if (userAgent.includes('Safari')) {
            message += t('binding.permission_safari');
        } else if (userAgent.includes('Edge')) {
            message += t('binding.permission_edge');
        } else {
            message += t('binding.permission_generic');
        }

        return message;
    }

    handleDisablePush = async () => {
        try {
            const pushManager = window.laneChatPushManager;
            if (!pushManager) {
                return;
            }

            await pushManager.unsubscribe();
        } catch (error) {
            alert(t('binding.push_disable_failed', { error: error.message }));
        }
    }

    render() {
        const { bindingData, theme } = this.props;

        if (!bindingData) return null;

        const { binding_type, status, code, email, message, deep_link: rawDeepLink } = bindingData;
        // TG 深链白名单：只放行 https:// 与 tg: scheme，其余当作不可点(#)兜底
        const safeDeepLink = /^(https:\/\/|tg:)/i.test(rawDeepLink || '') ? rawDeepLink : null;

        const styles = {
            container: {
                backgroundColor: 'var(--lc-surface, #ffffff)',
                // 规范 §4.3：整卡不用主色描边（商户配红主题会整卡变红），
                // 收敛到与其余富卡逐字节相同的外框。
                border: 'var(--lc-hairline)',
                borderRadius: 'var(--lc-radius-md)',
                boxShadow: 'var(--lc-shadow-1)',
                padding: 'var(--lc-space-3)',
                margin: 'var(--lc-space-2) 0',
                maxWidth: '320px',
            },
            header: {
                display: 'flex',
                alignItems: 'center',
                marginBottom: '10px',
                color: 'var(--lc-main)',
                fontWeight: 'bold',
            },
            icon: {
                width: '24px',
                height: '24px',
                marginRight: 'var(--lc-space-2)',
            },
            message: {
                marginBottom: '10px',
                color: 'var(--lc-text, #1e293b)',
                lineHeight: '1.5',
            },
            codeBox: {
                backgroundColor: 'var(--lc-surface-2, #f1f5f9)',
                border: 'var(--lc-hairline)',
                borderRadius: 'var(--lc-radius-xs)',
                padding: '10px',
                fontFamily: 'monospace',
                fontSize: '14px',
                marginBottom: '10px',
                wordBreak: 'break-all',
                color: 'var(--lc-text, #1e293b)',
            },
            button: {
                backgroundColor: 'var(--lc-main)',
                color: 'var(--lc-bubble-user-text, #ffffff)',
                border: 'none',
                borderRadius: 'var(--lc-radius-xs)',
                padding: 'var(--lc-space-2) var(--lc-space-4)',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'opacity var(--lc-dur-base)',
            },
            statusBadge: {
                display: 'inline-block',
                padding: 'var(--lc-space-1) var(--lc-space-3)',
                borderRadius: 'var(--lc-radius-full)',
                fontSize: '12px',
                fontWeight: 'bold',
                marginTop: 'var(--lc-space-2)',
            },
            // 颜色专项：前景原本两个都写死 '#ffffff'。琥珀底上的白字实测对比度
            // **2.15**（12px bold，AA 要 4.5），基本读不了 —— 这不是审美问题。
            // 取值就是 pickTextOn 的结果：theme-color.js 的 golden vectors 里
            // `#f59e0b → #0f1115`、`#16a34a → #ffffff`，这里直接落这两个值
            // （状态色亮暗恒定，所以它们的前景也恒定，不需要跟主题变）。
            successBadge: {
                backgroundColor: 'var(--lc-success)',
                color: '#ffffff',
            },
            pendingBadge: {
                backgroundColor: 'var(--lc-warning)',
                color: '#0f1115',
            },
            emailDisplay: {
                backgroundColor: 'var(--lc-surface-2, #f1f5f9)',
                padding: 'var(--lc-space-2) var(--lc-space-3)',
                borderRadius: 'var(--lc-radius-xs)',
                marginTop: 'var(--lc-space-2)',
                fontStyle: 'italic',
                color: 'var(--lc-text, #1e293b)',
            }
        };

        return (
            <div style={styles.container}>
                <div style={styles.header}>
                    {binding_type === 'telegram' ? (
                        <svg style={styles.icon} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M9.78,18.65L10.06,14.42L17.74,7.5C18.08,7.19 17.67,7.04 17.22,7.31L7.74,13.3L3.64,12C2.76,11.75 2.75,11.14 3.84,10.7L19.81,4.54C20.54,4.21 21.24,4.72 20.96,5.84L18.24,18.65C18.05,19.56 17.5,19.78 16.74,19.36L12.6,16.3L10.61,18.23C10.38,18.46 10.19,18.65 9.78,18.65Z" />
                        </svg>
                    ) : binding_type === 'push' ? (
                        <svg style={styles.icon} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M21,19V20H3V19L5,17V11C5,7.9 7.03,5.17 10,4.29C10,4.19 10,4.1 10,4A2,2 0 0,1 12,2A2,2 0 0,1 14,4C14,4.1 14,4.19 14,4.29C16.97,5.17 19,7.9 19,11V17L21,19M14,21A2,2 0 0,1 12,23A2,2 0 0,1 10,21" />
                        </svg>
                    ) : (
                        <svg style={styles.icon} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z" />
                        </svg>
                    )}
                    <span>{
                        binding_type === 'telegram' ? t('binding.title_telegram') :
                        binding_type === 'push' ? t('binding.title_push') :
                        t('binding.title_email')
                    }</span>
                </div>

                <div style={styles.message}>{message}</div>

                {rawDeepLink && binding_type === 'telegram' && (
                    <a
                        href={safeDeepLink || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            ...styles.button,
                            display: 'inline-block',
                            textDecoration: 'none',
                            textAlign: 'center',
                            background: 'linear-gradient(135deg, #0088cc, #0077b5)',
                        }}
                        onMouseOver={(e) => e.target.style.opacity = '0.8'}
                        onMouseOut={(e) => e.target.style.opacity = '1'}
                        onClick={(e) => {
                            if (!safeDeepLink) { e.preventDefault(); return; }
                            if (nativeBridge.isActive() && nativeBridge.supports('openUrl')) {
                                e.preventDefault();
                                openLink(safeDeepLink, { source: 'tg_binding' });
                            }
                        }}
                    >
                        {t('binding.open_telegram')}
                    </a>
                )}

                {code && (
                    <>
                        <div style={styles.codeBox}>
                            /verify {code}
                        </div>
                        <button
                            style={styles.button}
                            onClick={this.handleCopyCode}
                            onMouseOver={(e) => e.target.style.opacity = '0.8'}
                            onMouseOut={(e) => e.target.style.opacity = '1'}
                        >
                            {t('binding.copy_command')}
                        </button>
                    </>
                )}

                {email && (
                    <div style={{ ...styles.emailDisplay, display: 'flex', alignItems: 'center', gap: 'var(--lc-space-2)' }}>
                        <LcIcon name="mail" size={14} />
                        <span>{email}</span>
                    </div>
                )}

                {binding_type === 'push' && (
                    <>
                        {status === 'request' && (
                            <button
                                style={styles.button}
                                onClick={this.handleEnablePush}
                                onMouseOver={(e) => e.target.style.opacity = '0.8'}
                                onMouseOut={(e) => e.target.style.opacity = '1'}
                            >
                                {t('binding.enable_push_btn')}
                            </button>
                        )}
                        {status === 'success' && (
                            <button
                                style={{ ...styles.button, backgroundColor: '#666' }}
                                onClick={this.handleDisablePush}
                                onMouseOver={(e) => e.target.style.opacity = '0.8'}
                                onMouseOut={(e) => e.target.style.opacity = '1'}
                            >
                                {t('binding.disable_push_btn')}
                            </button>
                        )}
                        {status === 'permission-denied' && (
                            <button
                                style={{ ...styles.button, backgroundColor: 'var(--lc-danger)' }}
                                onClick={() => alert(this.getPermissionDeniedMessage())}
                                onMouseOver={(e) => e.target.style.opacity = '0.8'}
                                onMouseOut={(e) => e.target.style.opacity = '1'}
                            >
                                {t('binding.view_permission_help')}
                            </button>
                        )}
                    </>
                )}

                <div style={{
                    ...styles.statusBadge,
                    ...(status === 'success' ? styles.successBadge :
                       status === 'request' ? styles.pendingBadge :
                       status === 'unsubscribed' ? { backgroundColor: '#666', color: 'white' } :
                       status === 'permission-denied' ? { backgroundColor: 'var(--lc-danger)', color: '#ffffff' } :
                       styles.pendingBadge)
                }}>
                    {status === 'success' ? t('binding.status_enabled') :
                     status === 'request' ? t('binding.status_pending') :
                     status === 'unsubscribed' ? t('binding.status_off') :
                     status === 'permission-denied' ? t('binding.status_denied') :
                     t('binding.status_waiting')}
                </div>
            </div>
        );
    }
}
